// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { startAuthentication } from "@simplewebauthn/browser";
import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthError, authClient } from "@/lib/auth/auth-client";
import { userHasPermission } from "@/lib/auth/page-permissions";
import type { AuthContextResponse, AuthState, AuthUser, LoginCredentials } from "@/lib/auth/types";

/** A login attempt that succeeded credentials-wise but can't complete yet: the backend requires a
 * follow-up step. `tempToken` is the short-lived token authorizing that step (MFA verify or password
 * change). `undefined` from `login` means the session is fully established. */
export type LoginPendingAction = { action: "twoFactor" | "changePassword"; tempToken: string };

export interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<LoginPendingAction | undefined>;
  loginWithMfa: (mfaCode: string, tempToken: string) => Promise<void>;
  loginWithSSO: (state: string) => Promise<void>;
  loginWithPasskey: (email?: string) => Promise<void>;
  logout: () => Promise<void>;
  /** True from an interactive login until acknowledged — false for a session restored on page load. */
  isFreshLogin: boolean;
  acknowledgeFreshLogin: () => void;
  hasPermission: (permission: string) => boolean;
  hasFeatureFlag: (flag: string) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

function mapContextToUser(ctx: AuthContextResponse): AuthUser {
  return {
    userId: ctx.user.userId,
    publicId: ctx.user.publicId,
    email: ctx.user.email,
    avatar: ctx.user.avatar,
    fullName: ctx.user.fullName,
    groups: ctx.groups,
    permissions: ctx.permissions,
    featureFlags: ctx.featureFlags,
    realm: ctx.realm,
    hasMfa: ctx.hasMfa,
    hasPasskey: ctx.hasPasskey,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });
  const [isFreshLogin, setIsFreshLogin] = useState(false);

  // Restore session from stored token
  useEffect(function restoreSessionFromToken() {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setState({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }

    authClient
      .getContext()
      .then((ctx) => {
        setState({
          user: mapContextToUser(ctx),
          isAuthenticated: true,
          isLoading: false,
        });
      })
      .catch((err) => {
        if (err instanceof AuthError && err.status === 401) {
          // Token is truly invalid — clear and redirect to login
          localStorage.removeItem("access_token");
          sessionStorage.removeItem("debug_mode");
          setState({ user: null, isAuthenticated: false, isLoading: false });
        } else {
          // Transient error (network, 500, etc.) — retry after a delay
          setTimeout(() => {
            authClient
              .getContext()
              .then((ctx) => {
                setState({ user: mapContextToUser(ctx), isAuthenticated: true, isLoading: false });
              })
              .catch(() => {
                localStorage.removeItem("access_token");
                setState({ user: null, isAuthenticated: false, isLoading: false });
              });
          }, 2000);
        }
      });
  }, []);

  // Every login path lands here. The query cache is in-memory and outlives logout, so a different
  // user logging in on the same tab would otherwise be served the previous user's responses.
  const startSession = useCallback(
    async (token: string) => {
      queryClient.clear();
      localStorage.setItem("access_token", token);
      const ctx = await authClient.getContext();
      setState({ user: mapContextToUser(ctx), isAuthenticated: true, isLoading: false });
      setIsFreshLogin(true);
    },
    [queryClient],
  );

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      const res = await authClient.login(credentials);

      // Credentials are good but the backend wants a follow-up step first (verify a code, or set a new
      // password). Hand the temp token back to the caller instead of establishing a session.
      if (res.action === "twoFactor" || res.action === "changePassword") {
        return { action: res.action, tempToken: res.token };
      }

      await startSession(res.token);
    },
    [startSession],
  );

  const loginWithMfa = useCallback(
    async (mfaCode: string, tempToken: string) => {
      const res = await authClient.verifyTwoFactor(mfaCode, tempToken);
      await startSession(res.token);
    },
    [startSession],
  );

  const loginWithSSO = useCallback(
    async (ssoState: string) => {
      const res = await authClient.claimState(ssoState);
      await startSession(res.token);
    },
    [startSession],
  );

  const loginWithPasskey = useCallback(
    async (email?: string) => {
      // Step 1: challenge from the server (scoped to the email's credentials, or discoverable).
      const { sessionKey, options } = await authClient.passkeyAuthBegin(email);
      // Step 2: the browser WebAuthn ceremony — must run in response to a user gesture.
      const credential = await startAuthentication({
        optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON,
      });
      // Step 3: verify with the server and receive a session token (same shape as login/MFA).
      const res = await authClient.passkeyAuthComplete(sessionKey, credential);
      await startSession(res.token);
    },
    [startSession],
  );

  const logout = useCallback(async () => {
    // Fire the logout request in the background (it reads the current token synchronously, before
    // we clear it) — best-effort, errors ignored. The UI logs out immediately below.
    authClient.logout().catch(() => {});
    localStorage.removeItem("access_token");
    sessionStorage.removeItem("debug_mode");
    setState({ user: null, isAuthenticated: false, isLoading: false });
    setIsFreshLogin(false);
  }, []);

  const acknowledgeFreshLogin = useCallback(() => setIsFreshLogin(false), []);

  const hasPermission = useCallback((permission: string) => userHasPermission(state.user, permission), [state.user]);

  const hasFeatureFlag = useCallback(
    (flag: string) => {
      if (!state.user) return false;
      return !!state.user.featureFlags[flag];
    },
    [state.user],
  );

  const value = useMemo(
    () => ({
      ...state,
      login,
      loginWithMfa,
      loginWithSSO,
      loginWithPasskey,
      logout,
      isFreshLogin,
      acknowledgeFreshLogin,
      hasPermission,
      hasFeatureFlag,
    }),
    [
      state,
      login,
      loginWithMfa,
      loginWithSSO,
      loginWithPasskey,
      logout,
      isFreshLogin,
      acknowledgeFreshLogin,
      hasPermission,
      hasFeatureFlag,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
